import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, LinearProgress, Chip, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Collapse, TextField, MenuItem,
  Alert, Tooltip, CircularProgress,
} from '@mui/material';
import {
  CheckCircle, Cancel, Warning, ExpandMore, ExpandLess,
  AutoFixHigh, Close, PlayArrow, Stop, Visibility,
} from '@mui/icons-material';
import { AdminService } from '../../services/adminService';
import EditableExerciseContentPanel from './EditableExerciseContentPanel';

const SEVERITY_COLOR = { low: 'info', medium: 'warning', high: 'error' };
const SEVERITY_LABEL = { low: 'Mineur', medium: 'Moyen', high: 'Critique' };

const normalizeBulkCorrectionFilters = (filters = {}) => {
  const normalizedFilters = { ...filters };

  if (Object.prototype.hasOwnProperty.call(normalizedFilters, 'active')) {
    normalizedFilters.isActive = normalizedFilters.active;
    delete normalizedFilters.active;
  }

  if (Object.prototype.hasOwnProperty.call(normalizedFilters, 'flagged')) {
    normalizedFilters.isFlagged = normalizedFilters.flagged;
    delete normalizedFilters.flagged;
  }

  return normalizedFilters;
};
const ISSUE_TYPE_LABEL = {
  sense: 'Sens',
  coherence: 'Cohérence/Alignement',
  correctness: 'Exactitude',
  structure: 'Structure',
  shortcut: 'Anti-raccourci',
  ambiguity: 'Ambiguïté',
};

// Distingue les défauts repérés par le contrôle DÉTERMINISTE (gate) de ceux du LLM.
const IssueSourceChip = ({ source }) => (
  <Chip
    label={source === 'deterministic' ? 'Déterministe' : 'LLM'}
    size="small"
    variant="outlined"
    color={source === 'deterministic' ? 'primary' : 'default'}
    sx={{ ml: 1, height: 16, fontSize: 10 }}
  />
);
const VALIDATION_STATUS_LABEL = {
  nouveau: 'Nouveau',
  en_attente: 'En attente',
  valide: 'Validé',
};

const GAME_TYPE_LABELS = {
  speed_game: 'Speed Game',
  text_holes: 'Texte à trous',
  correction_game: 'Correction',
  word_lists: 'Liste de mots',
  dictation: 'Dictée',
  association: 'Association',
  categorisation: 'Catégorisation',
  speed_sort: 'Speed Sort',
  word_choice: 'Dictée guidée',
};

function ExerciseResultRow({ result, onApply, onPreview, isValidated, isPendingReview }) {
  const [expanded, setExpanded] = useState(false);
  const [applying, setApplying] = useState(false);

  const handleApply = async () => {
    if (!result.correctedContent) return;
    setApplying(true);
    try {
      await onApply(result.exerciseId, result.correctedContent);
    } finally {
      setApplying(false);
    }
  };

  if (result.type === 'error') {
    const canPreviewError = Boolean(result.originalContent);
    return (
      <TableRow sx={{ bgcolor: 'rgba(211,47,47,0.05)' }}>
        <TableCell>{result.exerciseId}</TableCell>
        <TableCell>{result.title}</TableCell>
        <TableCell>
          {result.gameType ? (
            <Chip
              label={GAME_TYPE_LABELS[result.gameType] || result.gameType}
              size="small"
              variant="outlined"
            />
          ) : '—'}
        </TableCell>
        <TableCell><Chip label="Erreur" color="error" size="small" /></TableCell>
        <TableCell>
          {canPreviewError && (
            <Tooltip title="Voir le contenu">
              <IconButton size="small" color="info" onClick={() => onPreview(result)}>
                <Visibility fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="caption" color="error">{result.error}</Typography>
        </TableCell>
      </TableRow>
    );
  }

  const highSeverityCount = (result.issues || []).filter((i) => i.severity === 'high').length;
  const medSeverityCount = (result.issues || []).filter((i) => i.severity === 'medium').length;
  const canPreview = Boolean(result.originalContent);
  const itemResults = result.itemResults || [];
  const invalidItems = itemResults.filter((item) => !item.isValid);
  const itemSummary = result.itemSummary;
  const hasExpandableDetail = (result.issues?.length > 0) || invalidItems.length > 0;

  return (
    <>
      <TableRow
        hover
        sx={{ cursor: hasExpandableDetail ? 'pointer' : 'default', bgcolor: result.isValid ? undefined : 'rgba(255,152,0,0.05)' }}
        onClick={() => hasExpandableDetail && setExpanded((v) => !v)}
      >
        <TableCell>
          <Typography variant="body2" fontWeight="bold">{result.exerciseId}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{result.title || '(sans titre)'}</Typography>
        </TableCell>
        <TableCell>
          <Chip
            label={GAME_TYPE_LABELS[result.gameType] || result.gameType}
            size="small"
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          {result.isValid ? (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Chip icon={<CheckCircle />} label="OK" color="success" size="small" />
              {itemSummary && (
                <Chip
                  label={`${itemSummary.valid}/${itemSummary.total} sous-ex.`}
                  size="small"
                  variant="outlined"
                  color="success"
                />
              )}
              {isValidated && (
                <Chip label="Validé" color="success" size="small" variant="outlined" />
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {itemSummary && (
                <Chip
                  label={`${itemSummary.valid}/${itemSummary.total} sous-ex. OK`}
                  size="small"
                  variant="outlined"
                  color={itemSummary.invalid > 0 ? 'warning' : 'success'}
                />
              )}
              {highSeverityCount > 0 && (
                <Chip label={`${highSeverityCount} critique${highSeverityCount > 1 ? 's' : ''}`} color="error" size="small" />
              )}
              {medSeverityCount > 0 && (
                <Chip label={`${medSeverityCount} moyen${medSeverityCount > 1 ? 's' : ''}`} color="warning" size="small" />
              )}
              {result.issues.length - highSeverityCount - medSeverityCount > 0 && (
                <Chip label={`${result.issues.length - highSeverityCount - medSeverityCount} mineur${result.issues.length - highSeverityCount - medSeverityCount > 1 ? 's' : ''}`} color="info" size="small" />
              )}
              {isPendingReview && (
                <>
                  <Chip label="En attente" color="warning" size="small" variant="outlined" />
                  <Chip label="Signalé" color="warning" size="small" />
                </>
              )}
            </Box>
          )}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {canPreview && (
              <Tooltip title={result.correctedContent ? 'Aperçu avant / après' : 'Voir le contenu'}>
                <IconButton size="small" color="info" onClick={(e) => { e.stopPropagation(); onPreview(result); }}>
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!result.isValid && result.correctedContent && (
              result.applied ? (
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip label="Appliqué" color="primary" size="small" icon={<AutoFixHigh />} />
                  {isPendingReview ? (
                    <Chip label="En attente" color="warning" size="small" variant="outlined" />
                  ) : isValidated && (
                    <Chip label="Validé" color="success" size="small" variant="outlined" />
                  )}
                </Box>
              ) : (
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  startIcon={applying ? <CircularProgress size={12} /> : <AutoFixHigh />}
                  onClick={(e) => { e.stopPropagation(); handleApply(); }}
                  disabled={applying}
                >
                  Appliquer
                </Button>
              )
            )}
          </Box>
        </TableCell>
        <TableCell>
          {hasExpandableDetail && (
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          )}
        </TableCell>
      </TableRow>

      {expanded && hasExpandableDetail && (
        <TableRow>
          <TableCell colSpan={6} sx={{ py: 0 }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ py: 1.5, px: 2 }}>
                {invalidItems.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Sous-éléments à corriger ({invalidItems.length}
                      {itemSummary ? ` / ${itemSummary.total}` : ''})
                    </Typography>
                    {invalidItems.map((item) => (
                      <Box
                        key={item.index}
                        sx={{
                          mb: 1,
                          pl: 1.5,
                          borderLeft: 2,
                          borderColor: 'warning.main',
                        }}
                      >
                        <Typography variant="caption" fontWeight="bold" display="block">
                          #{item.index + 1} — {item.label}
                        </Typography>
                        {item.usagePhrase && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                            {item.usagePhrase}
                          </Typography>
                        )}
                        {item.issues.map((issue, issueIdx) => (
                          <Alert
                            key={`${item.index}-${issueIdx}`}
                            severity={SEVERITY_COLOR[issue.severity] || 'info'}
                            sx={{ mb: 0.5 }}
                            icon={<Warning fontSize="small" />}
                          >
                            <Typography variant="caption" fontWeight="bold">
                              [{ISSUE_TYPE_LABEL[issue.type] || issue.type}]
                            </Typography>{' '}
                            <Typography variant="caption">{issue.description}</Typography>
                          </Alert>
                        ))}
                      </Box>
                    ))}
                  </Box>
                )}

                {itemSummary && invalidItems.length === 0 && itemResults.length > 0 && (
                  <Typography variant="caption" color="success.main" display="block" sx={{ mb: 1 }}>
                    Les {itemSummary.total} sous-éléments sont structurellement corrects.
                  </Typography>
                )}

                {result.issues?.length > 0 && (
                  <Box>
                    {invalidItems.length > 0 && (
                      <Typography variant="subtitle2" gutterBottom>
                        Problèmes globaux
                      </Typography>
                    )}
                    {result.issues.map((issue, idx) => (
                      <Alert
                        key={idx}
                        severity={SEVERITY_COLOR[issue.severity] || 'info'}
                        sx={{ mb: 0.5 }}
                        icon={<Warning fontSize="small" />}
                      >
                        <Typography variant="caption" fontWeight="bold">
                          [{ISSUE_TYPE_LABEL[issue.type] || issue.type}]
                        </Typography>{' '}
                        <Typography variant="caption">{issue.description}</Typography>
                        <Chip
                          label={SEVERITY_LABEL[issue.severity] || issue.severity}
                          size="small"
                          color={SEVERITY_COLOR[issue.severity] || 'default'}
                          sx={{ ml: 1, height: 16, fontSize: 10 }}
                        />
                        <IssueSourceChip source={issue.source} />
                      </Alert>
                    ))}
                  </Box>
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export default function BulkCorrectionModal({ open, onClose, currentFilters = {}, onExercisesUpdated }) {
  const [phase, setPhase] = useState('config'); // config | running | done
  const [maxItems, setMaxItems] = useState(20);
  const [autoApply, setAutoApply] = useState(false);
  const [runWasAutoApply, setRunWasAutoApply] = useState(false);
  const [results, setResults] = useState([]);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [summary, setSummary] = useState(null);
  const [analyzing, setAnalyzing] = useState('');
  const abortRef = useRef(null);
  const [appliedIds, setAppliedIds] = useState(new Set());
  const [validatedIds, setValidatedIds] = useState(new Set());
  const [pendingReviewIds, setPendingReviewIds] = useState(new Set());
  const [applyError, setApplyError] = useState(null);
  const [validateError, setValidateError] = useState(null);
  const [didMutateExercises, setDidMutateExercises] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewEdits, setPreviewEdits] = useState({
    original: null,
    corrected: null,
    originalError: null,
    correctedError: null,
  });
  const previewExerciseIdRef = useRef(null);

  useEffect(() => {
    if (!previewResult) {
      previewExerciseIdRef.current = null;
      setPreviewEdits({
        original: null,
        corrected: null,
        originalError: null,
        correctedError: null,
      });
      return;
    }

    previewExerciseIdRef.current = previewResult.exerciseId;
    setPreviewEdits({
      original: previewResult.originalContent ?? null,
      corrected: previewResult.correctedContent ?? null,
      originalError: null,
      correctedError: null,
    });
  }, [previewResult]);

  const syncResultContent = useCallback((exerciseId, patch) => {
    setResults((prev) =>
      prev.map((r) => (r.exerciseId === exerciseId ? { ...r, ...patch } : r))
    );
  }, []);

  const handlePreviewOriginalChange = useCallback((parsed, error) => {
    setPreviewEdits((prev) => ({ ...prev, original: parsed, originalError: error }));
    const exerciseId = previewExerciseIdRef.current;
    if (exerciseId && parsed) {
      syncResultContent(exerciseId, { originalContent: parsed });
    }
  }, [syncResultContent]);

  const handlePreviewCorrectedChange = useCallback((parsed, error) => {
    setPreviewEdits((prev) => ({ ...prev, corrected: parsed, correctedError: error }));
    const exerciseId = previewExerciseIdRef.current;
    if (exerciseId && parsed) {
      syncResultContent(exerciseId, { correctedContent: parsed });
    }
  }, [syncResultContent]);

  const handleClosePreview = () => {
    setPreviewResult(null);
  };

  const handleApplyOne = async (exerciseId, content, { manualValidate = false } = {}) => {
    await AdminService.applyBulkCorrections(
      [{ exerciseId, correctedContent: content }],
      { manualValidate },
    );
    setAppliedIds((prev) => new Set([...prev, exerciseId]));
    setDidMutateExercises(true);
    if (manualValidate) {
      setValidatedIds((prev) => new Set([...prev, exerciseId]));
      setPendingReviewIds((prev) => {
        const next = new Set(prev);
        next.delete(exerciseId);
        return next;
      });
    } else {
      setPendingReviewIds((prev) => new Set([...prev, exerciseId]));
    }
    setResults((prev) =>
      prev.map((r) => (
        r.exerciseId === exerciseId
          ? {
            ...r,
            applied: true,
            validated: manualValidate,
            pendingReview: !manualValidate,
          }
          : r
      ))
    );
    onExercisesUpdated?.();
  };

  const handleApplyFromPreview = async (which) => {
    if (!previewResult?.exerciseId || previewResult.applied) return;

    const content = which === 'corrected' ? previewEdits.corrected : previewEdits.original;
    const error = which === 'corrected' ? previewEdits.correctedError : previewEdits.originalError;
    if (!content || error) return;

    await handleApplyOne(previewResult.exerciseId, content, { manualValidate: true });
    setPreviewResult((prev) => (prev ? { ...prev, applied: true, validated: true, pendingReview: false } : prev));
    handleClosePreview();
  };

  const handleValidateAllOk = async () => {
    const pending = results.filter(
      (r) => r.type === 'result' && r.isValid && !validatedIds.has(r.exerciseId)
    );
    if (pending.length === 0) return;

    setValidateError(null);
    try {
      const response = await AdminService.validateBulkExercises(pending.map((r) => r.exerciseId));
      const validatedSet = new Set(response?.validated || pending.map((r) => r.exerciseId));
      setValidatedIds((prev) => new Set([...prev, ...validatedSet]));
      setDidMutateExercises(true);
      onExercisesUpdated?.();
      if (response?.errors?.length > 0) {
        setValidateError(
          `${response.errors.length} validation(s) échouée(s) : ${response.errors.map((e) => `#${e.exerciseId}`).join(', ')}`
        );
      }
    } catch (err) {
      setValidateError(`Erreur lors de la validation : ${err.message}`);
    }
  };

  const handleStart = useCallback(async () => {
    setPhase('running');
    setRunWasAutoApply(autoApply);
    setResults([]);
    setProgress({ current: 0, total: 0 });
    setSummary(null);

    let response;
    try {
      response = await AdminService.startBulkCorrection({
        filters: normalizeBulkCorrectionFilters(currentFilters),
        maxItems,
        autoApply,
      });
    } catch (err) {
      setResults([{ type: 'error', exerciseId: 0, title: 'Connexion', error: err.message }]);
      setPhase('done');
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    abortRef.current = reader;
    let buffer = '';

    try {
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          let event;
          try { event = JSON.parse(line.slice(6)); } catch { continue; }

          if (event.type === 'start') {
            setProgress({ current: 0, total: event.total });
          } else if (event.type === 'progress') {
            setAnalyzing(event.title || `#${event.exerciseId}`);
            setProgress((p) => ({ ...p, current: p.current }));
          } else if (event.type === 'result' || event.type === 'error') {
            setResults((prev) => [...prev, event]);
            setProgress((p) => ({ ...p, current: p.current + 1 }));
            if (event.applied) {
              setAppliedIds((prev) => new Set([...prev, event.exerciseId]));
              setDidMutateExercises(true);
            }
            if (event.validated) {
              setValidatedIds((prev) => new Set([...prev, event.exerciseId]));
              setDidMutateExercises(true);
            }
            if (event.pendingReview) {
              setPendingReviewIds((prev) => new Set([...prev, event.exerciseId]));
              setDidMutateExercises(true);
            }
          } else if (event.type === 'complete' || event.type === 'fatal_error') {
            setSummary(event);
            setPhase('done');
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setResults((prev) => [...prev, { type: 'error', exerciseId: 0, title: 'Stream', error: err.message }]);
      }
      setPhase('done');
    }
  }, [currentFilters, maxItems, autoApply]);

  const handleStop = () => {
    if (abortRef.current) {
      try { abortRef.current.cancel(); } catch {}
    }
    setPhase('done');
  };

  const handleApplyAll = async () => {
    const items = results
      .filter((r) => r.type === 'result')
      .filter((r) => {
        if (r.isValid) return !validatedIds.has(r.exerciseId);
        return !pendingReviewIds.has(r.exerciseId);
      })
      .map((r) => ({
        exerciseId: r.exerciseId,
        isValid: r.isValid,
        ...(!r.isValid && r.correctedContent && !appliedIds.has(r.exerciseId)
          ? { correctedContent: r.correctedContent }
          : {}),
      }));

    if (items.length === 0) return;
    setApplyError(null);
    try {
      const response = await AdminService.finalizeBulkSafeguard(items);
      const validatedSet = new Set(response?.validated || []);
      const appliedSet = new Set(response?.applied || []);
      const flaggedSet = new Set(response?.flagged || []);

      setValidatedIds((prev) => new Set([...prev, ...validatedSet]));
      setAppliedIds((prev) => new Set([...prev, ...appliedSet]));
      setPendingReviewIds((prev) => new Set([...prev, ...flaggedSet]));
      setDidMutateExercises(true);
      setResults((prev) =>
        prev.map((r) => {
          if (!items.some((item) => item.exerciseId === r.exerciseId)) return r;
          if (validatedSet.has(r.exerciseId)) {
            return { ...r, validated: true, pendingReview: false };
          }
          if (flaggedSet.has(r.exerciseId)) {
            return {
              ...r,
              applied: appliedSet.has(r.exerciseId),
              pendingReview: true,
              validated: false,
            };
          }
          return r;
        })
      );
      onExercisesUpdated?.();
      if (response?.errors?.length > 0) {
        setApplyError(
          `${response.errors.length} finalisation(s) échouée(s) : ${response.errors.map((e) => `#${e.exerciseId}`).join(', ')}`
        );
      }
    } catch (err) {
      setApplyError(`Erreur lors de la finalisation : ${err.message}`);
    }
  };

  const handleClose = () => {
    if (phase === 'running') handleStop();
    setPhase('config');
    setResults([]);
    setSummary(null);
    setProgress({ current: 0, total: 0 });
    setAppliedIds(new Set());
    setValidatedIds(new Set());
    setPendingReviewIds(new Set());
    setRunWasAutoApply(false);
    setApplyError(null);
    setValidateError(null);
    if (didMutateExercises) {
      onExercisesUpdated?.();
    }
    setDidMutateExercises(false);
    setPreviewResult(null);
    onClose();
  };

  const handlePrepareFullAuto = () => {
    setAutoApply(true);
    setMaxItems(200);
    setResults([]);
    setSummary(null);
    setProgress({ current: 0, total: 0 });
    setApplyError(null);
    setValidateError(null);
    setAppliedIds(new Set());
    setValidatedIds(new Set());
    setPendingReviewIds(new Set());
    setDidMutateExercises(false);
    setRunWasAutoApply(false);
    setPhase('config');
  };

  const pendingFinalize = results.filter((r) => {
    if (r.type !== 'result') return false;
    if (r.isValid) return !validatedIds.has(r.exerciseId);
    return !pendingReviewIds.has(r.exerciseId);
  });
  const pendingValidations = results.filter(
    (r) => r.type === 'result' && r.isValid && !validatedIds.has(r.exerciseId)
  );
  const okValidatedCount = results.filter(
    (r) => r.type === 'result' && r.isValid && validatedIds.has(r.exerciseId)
  ).length;

  const activeFiltersCount = Object.values(currentFilters).filter(
    (v) => v !== '' && v !== null && v !== undefined
  ).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoFixHigh color="primary" />
          <Typography variant="h6">Correction en masse par IA</Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {phase === 'config' && (
          <Box>
            {autoApply ? (
              <Alert severity="warning" sx={{ mb: 3 }}>
                <strong>Mode correction automatique</strong> — les exercices sans problème seront{' '}
                <strong>validés</strong>. Les autres seront corrigés si possible, puis marqués{' '}
                <strong>« en attente de validation »</strong> et <strong>signalés</strong> pour relecture ultérieure.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 3 }}>
                L'IA va analyser chaque exercice et vérifier : <strong>le sens</strong>,{' '}
                <strong>la cohérence avec le thème</strong> et{' '}
                <strong>l'exactitude des réponses</strong> (orthographe, solutions, corrections).
              </Alert>
            )}

            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 3 }}>
              <Box sx={{ flex: '1 1 200px' }}>
                <TextField
                  select
                  fullWidth
                  label="Nombre max d'exercices"
                  value={maxItems}
                  onChange={(e) => setMaxItems(Number(e.target.value))}
                  helperText="Les exercices les plus récents sont analysés en premier"
                >
                  {[10, 20, 50, 100, 200].map((n) => (
                    <MenuItem key={n} value={n}>{n} exercices</MenuItem>
                  ))}
                </TextField>
              </Box>
              <Box sx={{ flex: '1 1 200px' }}>
                <TextField
                  select
                  fullWidth
                  label="Appliquer automatiquement"
                  value={autoApply ? 'yes' : 'no'}
                  onChange={(e) => setAutoApply(e.target.value === 'yes')}
                  helperText={autoApply ? 'OK validés ; problèmes en attente + signalés' : 'Vous choisirez quelles corrections appliquer'}
                >
                  <MenuItem value="no">Non (révision manuelle)</MenuItem>
                  <MenuItem value="yes">Oui (appliqué automatiquement)</MenuItem>
                </TextField>
              </Box>
            </Box>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Filtres actifs ({activeFiltersCount})
              </Typography>
              {activeFiltersCount === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Aucun filtre — tous les exercices seront analysés (limité à {maxItems})
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {currentFilters.gameType && <Chip label={`Type: ${currentFilters.gameType}`} size="small" />}
                  {currentFilters.source && <Chip label={`Source: ${currentFilters.source}`} size="small" />}
                  {currentFilters.active && <Chip label={`Statut: ${currentFilters.active === 'true' ? 'Actif' : 'Inactif'}`} size="small" />}
                  {currentFilters.search && <Chip label={`Recherche: "${currentFilters.search}"`} size="small" />}
                  {currentFilters.flagged && <Chip label={`Signalés: ${currentFilters.flagged}`} size="small" />}
                  {currentFilters.validationStatus && (
                    <Chip
                      label={`Validation: ${VALIDATION_STATUS_LABEL[currentFilters.validationStatus] || currentFilters.validationStatus}`}
                      size="small"
                    />
                  )}
                  {currentFilters.domain !== undefined && (
                    <Chip label={`Domaine: ${currentFilters.domain || '(vide)'}`} size="small" />
                  )}
                  {currentFilters.competence !== undefined && (
                    <Chip label={`Compétence: ${currentFilters.competence || '(vide)'}`} size="small" />
                  )}
                  {currentFilters.notion !== undefined && (
                    <Chip label={`Notion: ${currentFilters.notion || '(vide)'}`} size="small" />
                  )}
                  {currentFilters.dateFrom && <Chip label={`Depuis: ${currentFilters.dateFrom}`} size="small" />}
                  {currentFilters.dateTo && <Chip label={`Jusqu'au: ${currentFilters.dateTo}`} size="small" />}
                </Box>
              )}
            </Paper>
          </Box>
        )}

        {(phase === 'running' || phase === 'done') && (
          <Box>
            {/* Barre de progression */}
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2">
                  {phase === 'running' ? (
                    <>Analyse en cours : <em>{analyzing}</em></>
                  ) : (
                    'Analyse terminée'
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {progress.current} / {progress.total}
                </Typography>
              </Box>
              <LinearProgress
                variant={progress.total > 0 ? 'determinate' : 'indeterminate'}
                value={progress.total > 0 ? (progress.current / progress.total) * 100 : 0}
                color={phase === 'done' ? 'success' : 'primary'}
              />
            </Box>

            {/* Résumé final */}
            {applyError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApplyError(null)}>
                {applyError}
              </Alert>
            )}
            {validateError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setValidateError(null)}>
                {validateError}
              </Alert>
            )}

            {phase === 'done' && summary && (
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip
                  icon={<CheckCircle />}
                  label={`${summary.total - (summary.withIssues || 0)} exercices corrects`}
                  color="success"
                />
                {summary.withIssues > 0 && (
                  <Chip
                    icon={<Warning />}
                    label={`${summary.withIssues} exercice${summary.withIssues > 1 ? 's' : ''} avec problèmes`}
                    color="warning"
                  />
                )}
                {summary.applied > 0 && (
                  <Chip
                    icon={<AutoFixHigh />}
                    label={`${summary.applied} correction${summary.applied > 1 ? 's' : ''} appliquée${summary.applied > 1 ? 's' : ''} auto`}
                    color="primary"
                  />
                )}
                {(summary.validated > 0 || okValidatedCount > 0) && (
                  <Chip
                    icon={<CheckCircle />}
                    label={`${summary.validated ?? okValidatedCount} validé${(summary.validated ?? okValidatedCount) > 1 ? 's' : ''}`}
                    color="success"
                    variant="outlined"
                  />
                )}
                {summary.flagged > 0 && (
                  <Chip
                    icon={<Warning />}
                    label={`${summary.flagged} en attente / signalé${summary.flagged > 1 ? 's' : ''}`}
                    color="warning"
                    variant="outlined"
                  />
                )}
                {summary.errors > 0 && (
                  <Chip
                    icon={<Cancel />}
                    label={`${summary.errors} erreur${summary.errors > 1 ? 's' : ''}`}
                    color="error"
                  />
                )}
              </Box>
            )}

            {/* Tableau des résultats */}
            {results.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 450 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Titre</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Statut</TableCell>
                      <TableCell>Actions</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((result, idx) => (
                      <ExerciseResultRow
                        key={`${result.exerciseId}-${idx}`}
                        result={appliedIds.has(result.exerciseId) ? { ...result, applied: true } : result}
                        isValidated={validatedIds.has(result.exerciseId)}
                        isPendingReview={pendingReviewIds.has(result.exerciseId)}
                        onApply={handleApplyOne}
                        onPreview={setPreviewResult}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Dialog aperçu — contenu éditable, comparaison avant / après */}
      <Dialog open={!!previewResult} onClose={handleClosePreview} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {previewResult?.correctedContent
              ? `Comparaison avant / après — ${previewResult?.title || `#${previewResult?.exerciseId}`}`
              : `Contenu de l'exercice — ${previewResult?.title || `#${previewResult?.exerciseId}`}`}
          </Typography>
          <IconButton size="small" onClick={handleClosePreview}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            Basculez sur l&apos;onglet <strong>JSON</strong> pour modifier le contenu, puis appliquez la version souhaitée.
          </Alert>
          {previewResult?.type === 'error' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              L&apos;analyse a échoué : {previewResult.error}
            </Alert>
          )}
          {previewResult?.isValid && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Aucun problème détecté par l&apos;analyse IA.
            </Alert>
          )}
          {previewResult?.issues?.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {previewResult.issues.map((issue, idx) => (
                <Alert
                  key={idx}
                  severity={SEVERITY_COLOR[issue.severity] || 'info'}
                  sx={{ mb: 0.5 }}
                  icon={<Warning fontSize="small" />}
                >
                  <Typography variant="caption" fontWeight="bold">
                    [{ISSUE_TYPE_LABEL[issue.type] || issue.type}]
                  </Typography>{' '}
                  <Typography variant="caption">{issue.description}</Typography>
                  <IssueSourceChip source={issue.source} />
                </Alert>
              ))}
            </Box>
          )}
          {previewResult?.correctedContent ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <EditableExerciseContentPanel
                  key={`original-${previewResult.exerciseId}`}
                  label="Avant (original)"
                  gameType={previewResult.gameType}
                  content={previewResult.originalContent}
                  onContentChange={handlePreviewOriginalChange}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <EditableExerciseContentPanel
                  key={`corrected-${previewResult.exerciseId}`}
                  label="Après (corrigé)"
                  chipColor="success"
                  borderColor="success.main"
                  gameType={previewResult.gameType}
                  content={previewResult.correctedContent}
                  onContentChange={handlePreviewCorrectedChange}
                />
              </Grid>
            </Grid>
          ) : (
            <EditableExerciseContentPanel
              key={`content-${previewResult?.exerciseId}`}
              label="Contenu actuel"
              gameType={previewResult?.gameType}
              content={previewResult?.originalContent}
              onContentChange={handlePreviewOriginalChange}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePreview}>Fermer</Button>
          {previewResult?.applied ? (
            <Chip label="Appliqué et validé" color="success" icon={<CheckCircle />} />
          ) : (
            <>
              <Button
                variant="outlined"
                color="primary"
                disabled={!previewEdits.original || previewEdits.originalError}
                onClick={() => handleApplyFromPreview('original')}
              >
                {previewResult?.correctedContent ? 'Appliquer l\'original (valide)' : 'Appliquer et valider'}
              </Button>
              {previewResult?.correctedContent && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AutoFixHigh />}
                  disabled={!previewEdits.corrected || previewEdits.correctedError}
                  onClick={() => handleApplyFromPreview('corrected')}
                >
                  Appliquer la correction (valide)
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        {phase === 'config' && (
          <>
            <Button onClick={handleClose}>Annuler</Button>
            <Button
              variant="contained"
              color={autoApply ? 'warning' : 'primary'}
              startIcon={<PlayArrow />}
              onClick={handleStart}
            >
              {autoApply
                ? `Auto-corriger (${maxItems} exercices max)`
                : `Lancer l'analyse (${maxItems} exercices max)`}
            </Button>
          </>
        )}
        {phase === 'running' && (
          <>
            <Button startIcon={<Stop />} color="error" onClick={handleStop}>
              Arrêter
            </Button>
          </>
        )}
        {phase === 'done' && (
          <>
            <Button onClick={handleClose}>Fermer</Button>
            {pendingValidations.length > 0 && (
              <Tooltip title="Marque les exercices sans problème comme validés en base (statut « validé »)">
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  onClick={handleValidateAllOk}
                >
                  Valider les exercices OK ({pendingValidations.length})
                </Button>
              </Tooltip>
            )}
            {pendingFinalize.length > 0 && (
              <Tooltip title="Valide les exercices OK ; applique les corrections possibles ; les autres passent en attente et sont signalés">
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<AutoFixHigh />}
                  onClick={handleApplyAll}
                >
                  Tout appliquer ({pendingFinalize.length})
                </Button>
              </Tooltip>
            )}
            {!runWasAutoApply && (
              <Tooltip title="Valide les OK et signale les exercices à problèmes (en attente de relecture)">
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<AutoFixHigh />}
                  onClick={handlePrepareFullAuto}
                >
                  Lancer en masse (auto)
                </Button>
              </Tooltip>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
